import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

export default function PrivacyPolicyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-2xl mx-auto px-6 py-8">
        <button data-testid="privacy-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-muted text-sm mb-6 hover:text-espresso transition-colors">
          <ArrowLeft size={16} /> Back
        </button>

        <h1 data-testid="privacy-title" className="font-heading text-3xl text-espresso mb-2">Privacy Policy</h1>
        <p className="text-text-muted text-sm mb-8">Last updated: February 2026</p>

        <div className="space-y-8 text-text-muted leading-relaxed text-sm">
          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">1. Information We Collect</h2>
            <p className="mb-3">When you use the HostBase guest portal, we collect the following information:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-espresso">Phone Number</strong> &ndash; Used for account verification via one-time passcode (OTP) and to send order confirmations.</li>
              <li><strong className="text-espresso">Name</strong> &ndash; Optionally provided by you or the property owner to personalize your experience.</li>
              <li><strong className="text-espresso">Stay Dates</strong> &ndash; Check-in and check-out dates associated with your reservation.</li>
              <li><strong className="text-espresso">Order Information</strong> &ndash; Details of services and products you purchase through the platform, including payment transaction records.</li>
              <li><strong className="text-espresso">Usage Data</strong> &ndash; Pages viewed, features used, and general interaction data to improve the platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">2. How We Use Your Information</h2>
            <p className="mb-3">We use the information we collect to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Verify your identity and grant access to your property's guest portal</li>
              <li>Process orders for services and products</li>
              <li>Send SMS confirmations for authentication and order updates</li>
              <li>Personalize your guest experience</li>
              <li>Improve and optimize the platform</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">3. SMS Communications</h2>
            <p>By providing your phone number, you consent to receive SMS messages for: verification codes (OTP), order confirmations, and property-related updates. Message and data rates may apply. Message frequency varies. You can opt out at any time by contacting the property manager.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">4. Payment Information</h2>
            <p>Payment processing is handled securely by Stripe. We do not store your credit card number, CVV, or full payment details on our servers. Stripe's privacy policy governs the handling of your payment information.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">5. Data Sharing</h2>
            <p className="mb-3">We do not sell your personal information. We may share your information with:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong className="text-espresso">Property Owners/Managers</strong> &ndash; To fulfill services and manage your stay</li>
              <li><strong className="text-espresso">Service Providers</strong> &ndash; Third-party vendors who help operate the platform (Twilio for SMS, Stripe for payments)</li>
              <li><strong className="text-espresso">Legal Requirements</strong> &ndash; When required by law or to protect our rights</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">6. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to provide our services and fulfill the purposes described in this policy. Order records are retained for accounting and legal compliance purposes. You may request deletion of your data by contacting us.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">7. Security</h2>
            <p>We implement reasonable security measures to protect your information, including encrypted data transmission (HTTPS), secure authentication tokens, and access controls. However, no method of transmission over the internet is 100% secure.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">8. Your Rights</h2>
            <p className="mb-3">You have the right to:</p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your personal information</li>
              <li>Opt out of SMS communications</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">9. Changes to This Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the updated policy on this page with a revised "Last updated" date.</p>
          </section>

          <section>
            <h2 className="font-heading text-lg text-espresso mb-3">10. Contact Us</h2>
            <p>If you have questions about this Privacy Policy or wish to exercise your rights, please contact the property manager through the guest portal or reach out to us at the contact information provided during your stay.</p>
          </section>
        </div>
      </div>
    </div>
  );
}
